import React, { useState, useEffect } from "react";
import {
  Box, TextField, Button, Typography, Divider, Card,
  Grid
} from "@mui/material";
import { db, auth, storage } from "../firebase";
import { collection, getDocs, query, where, Timestamp, orderBy, onSnapshot } from "firebase/firestore";
import { Document, Page, Text, View, StyleSheet, pdf } from "@react-pdf/renderer";
import { addDoc, } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";


function Header({ collapsed,
  setCollapsed,
  mobileMenuOpen,
  setMobileMenuOpen }) { // Pass data as props from your main state

  const signOut = () => {
    auth.signOut();
    localStorage.removeItem("email");
    localStorage.removeItem("password");
    window.location.href = "/login";
  };

  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState({ trips: [], payments: [], products: [] });
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [payModes, setPayModes] = useState([]);
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [staffAttendance, setStaffAttendance] = useState([]);
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      checkAndGenerateReport();
    }, 60000); // every 1 min

    return () => clearInterval(interval);
  }, []);

  const checkAndGenerateReport = async () => {
    const now = new Date();

    const hours = now.getHours();
    const minutes = now.getMinutes();

    const isValidTime =
      (hours === 21 && minutes >= 0) ||
      (hours === 21 && minutes === 15);

    if (!isValidTime) return;

    const todayStr = now.toISOString().split("T")[0];

    // ✅ LOCAL CHECK (avoid repeat in same session)
    const localFlag = localStorage.getItem("report_generated_" + todayStr);
    if (localFlag) {
      console.log("⚠️ Already generated (local)");
    }

    // ✅ FIRESTORE CHECK (main protection)
    const q = query(
      collection(db, "daily_reports"),
      where("date", "==", todayStr)
    );

    const snap = await getDocs(q);

    if (!snap.empty) {
      console.log("⚠️ Already generated (Firestore)");
      localStorage.setItem("report_generated_" + todayStr, "true");
      return;
    }

    console.log("🚀 Generating report...");

    const start = Timestamp.fromDate(new Date(fromDate + " 00:00:00"));
    const end = Timestamp.fromDate(new Date(toDate + " 23:59:59"));

    // 1. Define Queries
    const qTrips = query(collection(db, "trips"), where("grossDate", ">=", start), where("grossDate", "<=", end), orderBy("grossDate", "asc"));
    const qPay = query(collection(db, "customer_history"), where("date", ">=", start), where("date", "<=", end), orderBy("date", "asc"));
    const qPurchases = query(collection(db, "purchases"), where("purchasedate", ">=", fromDate), where("purchasedate", "<=", toDate), orderBy("purchasedate", "desc"));
    const qDealerPay = query(collection(db, "dealerPayments"), where("date", ">=", start), where("date", "<=", end), orderBy("date", "desc"));
    const qOtherExpense = query(collection(db, "otherExpenses"), where("date", ">=", start), where("date", "<=", end));
    const qStaff = getDocs(collection(db, "staff"));
    const qAttendance = getDocs(collection(db, "staffattendance"));

    const [
      tripSnap, paySnap, purchaseSnap, dealerPaySnap,
      dealerSnap, expenseSnap, staffSnap, attendanceSnap
    ] = await Promise.all([
      getDocs(qTrips),
      getDocs(qPay),
      getDocs(qPurchases),
      getDocs(qDealerPay),
      getDocs(collection(db, "dealers")),
      getDocs(qOtherExpense),
      qStaff,
      qAttendance
    ]);

    // 3. Process Dealers Map
    const dealerMap = {};
    dealerSnap.forEach(doc => { dealerMap[doc.id] = doc.data().dealerName; });

    // 4. Process Expenses (Fixed variable name from otherExpSnap to expenseSnap)
    // Note: Ensure headMap is defined somewhere in your component scope
    const headMap = {}; // Replace or fetch this if needed

    const processedExpenses = expenseSnap.docs.reduce((acc, doc) => {
      const d = doc.data();
      const amt = Number(d.amount || 0);
      const headName = (typeof headMap !== 'undefined' && headMap[d.expenseHeadId]) || "General";

      acc.grandTotal += amt;
      acc.list.push({
        id: doc.id,
        ...d,
        headName,
        displayDate: d.date?.toDate().toLocaleDateString('en-GB')
      });
      return acc;
    }, { grandTotal: 0, list: [] });

    // 5. Process Trips
    const trips = tripSnap.docs.map(doc => {
      const tripData = { id: doc.id, ...doc.data() };

      const calculatedTripTotal = (tripData.products || [])
        .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
      return { ...tripData, calculatedTotal: calculatedTripTotal };
    }).filter(trip => trip.calculatedTotal > 0);


    // 6. Process Bolder Data (using the same tripSnap)
    const bolderData = tripSnap.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(trip => trip.products?.some(p =>
        (p.productName || "").toLowerCase().includes("bolder")
      ));

    // 7. Process Payments
    const payments = paySnap.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(pData => !pData.tripId)
      .map(pData => ({
        ...pData,
        displayCustomer: pData.customerName || (typeof getCustName === 'function' ? getCustName(pData.customerId || pData.customer) : "Unknown"),
        displayMode: typeof getPayModeName === 'function' ? getPayModeName(pData.paymentMode || pData.payMode) : "N/A"
      }));

    // 8. Aggregated Products
    const productMap = {};
    trips.forEach(trip => {
      (trip.products || []).forEach(p => {
        const name = p.productName || "General";
        if (!productMap[name]) productMap[name] = { name, qty: 0, amount: 0, count: 0 };
        productMap[name].qty += Number(p.qty) || 0;
        productMap[name].amount += Number(p.amount) || 0;
        productMap[name].count += 1;
      });
    });

    // 9. Process Purchases & Dealer Payments
    const purchases = purchaseSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      displayDealer: dealerMap[doc.data().dealerId] || "Unknown Dealer",
      calculatedTotal: Number(doc.data().grandtotal || 0)
    }));

    const dealerPayments = dealerPaySnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      displayDealer: dealerMap[doc.data().dealerId] || doc.data().Dealer || "Unknown",
      displayMode: typeof getPayModeName === 'function' ? getPayModeName(doc.data().paymentMode || doc.data().payMode) : "N/A"
    }));

    const staffMap = {};
    staffSnap.forEach(doc => {
      staffMap[doc.id] = doc.data();
    });
    setStaffList(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    const filteredAttendance = attendanceSnap.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(a => a.date >= fromDate && a.date <= toDate);

    const salaryData = filteredAttendance.map(att => {
      const staff = staffMap[att.staffId] || {};
      const monthlySalary = Number(staff.payment || 0);
      const perDay = monthlySalary / 30;

      let earned = 0;
      if (att.status === "Present") earned = perDay;
      else if (att.status === "Half Day") earned = perDay / 2;
      else earned = 0;

      return {
        ...att,
        staffName: staff.name || att.staffName || "Unknown",
        dailySalary: earned
      };
    });

    const totalSalaryExpense = salaryData.reduce(
      (sum, s) => sum + s.dailySalary,
      0
    );


    let reporrrdata = ({
      trips,
      payments,
      purchases,
      dealerPayments,
      expenses: processedExpenses, // Added this back in
      products: Object.values(productMap),
      bolderData,
      staffAttendance: salaryData,
      totalSalaryExpense
    });

    setData(reporrrdata);

    await generateAndUploadReport(todayStr, reporrrdata);
    localStorage.setItem("report_generated_" + todayStr, "true");
  };

  const generateAndUploadReport = async (dateStr, reporrrdata) => {
    try {

      const blob = await pdf(
        <InvoicePDF invoiceData={reporrrdata} />
      ).toBlob();

      const storageRef = ref(storage, `reports/Master_Report_${dateStr}.pdf`);

      await uploadBytes(storageRef, blob);

      const downloadURL = await getDownloadURL(storageRef);

      const totalSaleAmount = reporrrdata.trips.reduce(
        (sum, t) => sum + (Number(t.calculatedTotal) || 0),
        0
      );

      const totalCollection = reporrrdata.payments.reduce(
        (sum, p) => sum + (Number(p.amount) || 0),
        0
      );

      const totalIncome = totalSaleAmount + totalCollection;

      const totalBolderExpense = (reporrrdata.bolderData || []).reduce((acc, trip) => {
        const tons = Number(trip.netWeight || 0) / 1000;
        return acc + (tons * 92);
      }, 0);


      const totalPurchaseExpense = (reporrrdata.purchases || []).reduce(
        (sum, p) => sum + (Number(p.calculatedTotal) || 0),
        0
      );

      const totalQty = reporrrdata.trips.reduce((sum, trip) =>
        sum + (trip.products || []).reduce((pSum, p) => pSum + (Number(p.qty) || 0), 0), 0
      );

      const totalOtherExpense = reporrrdata.expenses?.grandTotal || 0;

      const totalLightBill = 56666;

      const totalStaffSalary = reporrrdata.totalSalaryExpense || 0;

      const royaltyRate = 670;

      const totalRoyaltyExpense = totalQty * royaltyRate;

      const totalExpense =
        totalBolderExpense +
        totalPurchaseExpense +
        totalOtherExpense +
        totalLightBill +
        totalStaffSalary + totalRoyaltyExpense;


      // 4. Save in Firestore
      await addDoc(collection(db, "daily_reports"), {
        date: dateStr,
        totalIncome,
        totalExpense,
        fileUrl: downloadURL,
        whatsappStatus: "pending",
        createdAt: new Date()
      });

      const netProfit = totalIncome - totalExpense;

      await sendWhatsAppReport({
        date: dateStr,
        totalIncome,
        totalExpense,
        netProfit,
        fileUrl: downloadURL
      });


      console.log("✅ Report Generated & Stored");

    } catch (err) {
      console.error("❌ Error:", err);
    }
  };

  const sendWhatsAppReport = async (reportData) => {
    try {
      try {
        const phoneid = "1174916145695973";
        const accesstoken = "EAAKj5T0Mx1YBRNd6VhhufPP2ApIjZCsZBFOkl855np9VZBvPvZAZAj7axVAevZBiapxd1Iw38v0hhZBwB2knNeLYSVgjR3RqtPn164H2GQ3ljP46kQvAAq0gsl3lfKDh55ZCsmo5fGZCaUSPpCRqeFjVA6VUeIaiu0ZCNxPPMTsV4oxnnUz4TRoICdbiW0xsAncwZDZD";
        const todayDate = new Date().toLocaleDateString("en-IN");

        const countSnap = await getDocs(collection(db, "daily_report"));

        let mobile = 0;

        if (!countSnap.empty) {
          const firstDoc = countSnap.docs[0].data();
          mobile = Number(firstDoc.mobile || 0);
        }

        const payload = {
          messaging_product: "whatsapp",
          to: "918530851222",
          type: "template",
          template: {
            name: "daily_report",
            language: { code: "en" },
            components: [
              {
                type: "header",
                parameters: [
                  {
                    type: "document",
                    document: {
                      link: reportData.fileUrl,
                      filename: "Daily Report.pdf"
                    }
                  }
                ]
              },
              {
                type: "body",
                parameters: [
                  { type: "text", text: reportData.date },
                  { type: "text", text: Number(reportData.totalIncome || 0).toFixed(0) },
                  { type: "text", text: Number(reportData.totalExpense || 0).toFixed(0) },
                  { type: "text", text: Number(reportData.netProfit || 0).toFixed(0) }
                ]
              }
            ]
          }
        };

        const response = await fetch(`https://graph.facebook.com/v22.0/${phoneid}/messages`, {
          method: "POST",
          // mode: "no-cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accesstoken}`,
          },
          body: JSON.stringify(payload)
        });

        console.log(response)
        const data = await response.json();
        if (response.ok) {
          await addDoc(collection(db, "whatsapp_history"),
            { mobile: mobile, customerName: "Owner", invoiceNo: 0, type: "Daily Report", amount: 0, fileURL: "", status: "Sent", createdAt: Timestamp.now() });
        }
        else {
          await addDoc(collection(db, "whatsapp_history"),
            { mobile: mobile, customerName: "Owner", invoiceNo: 0, type: "Daily Report", amount: 0, fileURL: "", status: "Failed", error: data.error?.message, createdAt: Timestamp.now() });
        }

      } catch (error) {
        console.error("❌ Fetch Error:", error);
      }

    } catch (err) {
      console.error("WhatsApp Error:", err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const fetchExpenseHeads = async () => {
      const snap = await getDocs(collection(db, "expenseHeads"));
      setExpenseHeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchExpenseHeads();
  }, []);


  const loadData = async () => {

    const unsubCustomers = onSnapshot(collection(db, "customers"), snap => {
      setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubPayModes = onSnapshot(collection(db, "payment_modes"), snap => {
      setPayModes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubCustomers();
      unsubPayModes();
    };

  };

  // Helper: Get Names from Props
  const getCustName = (id) => customers.find(c => c.id === id)?.customerName || id || "Unknown";
  const getPayModeName = (id) => payModes.find(m => m.payMode === id)?.payMode;



  const InvoicePDF = ({ invoiceData }) => {

    const products = invoiceData.products || [];
    const cellStyle = { border: '1px solid #000', padding: '3px 6px', fontSize: '0.85rem', color: '#322626' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', backgroundColor: '#eeeeee', textAlign: 'center', fontSize: '0.9rem' };
    const nestedStyle = { ...cellStyle, border: 'none', fontSize: '0.75rem', padding: '2px 4px' };

    const paymentModeList = payModes.map(m => m.payMode); // ["Cash","PhonePe","RTGS"]

    const getPaymentByMode = (trip, mode) => {
      if (!trip.payments || trip.payments.length === 0) return 0;

      return trip.payments
        .filter(p => (p.mode) === mode)
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    };

    // TOTAL PER PAYMENT MODE
    const getTotalByMode = (mode) => {
      return invoiceData.trips.reduce((sum, trip) => {
        if (!trip.payments) return sum;

        const modeTotal = trip.payments
          .filter(p => p.mode === mode)
          .reduce((s, p) => s + (Number(p.amount) || 0), 0);

        return sum + modeTotal;
      }, 0);
    };

    // TOTAL PAID
    const totalPaid = invoiceData.payments.reduce(
      (sum, p) => sum + (Number(p.amount) || 0),
      0
    );

    const totalSale = invoiceData.trips.reduce(
      (sum, t) => sum + (Number(t.calculatedTotal) || 0),
      0
    );

    const totalPending = totalSale - totalPaid;

    const productTotals = invoiceData.products.reduce(
      (acc, p) => {
        acc.qty += Number(p.qty || 0);
        acc.amount += Number(p.amount || 0);
        acc.count += Number(p.count || 0);
        return acc;
      },
      { qty: 0, amount: 0, count: 0 }
    );

    const getTripPaid = (trip) => {
      return invoiceData.payments
        .filter(p => p.tripId === trip.id) // ensure you store tripId in payments
        .reduce((sum, p) => sum + Number(p.amount || 0), 0);
    };

    // 1. Calculate Total Qty for all products across all trips
    const totalQty = invoiceData.trips.reduce((sum, trip) =>
      sum + (trip.products || []).reduce((pSum, p) => pSum + (Number(p.qty) || 0), 0), 0
    );

    // 2. Total Paid specifically from the Trip Sales Log (Spot/Trip payments)
    const totalTripPaid = invoiceData.trips.reduce((sum, t) => sum + (Number(t.paidAmount) || 0), 0);

    // 3. Collection History Total (Purely from customer_history/backdate entries)
    const totalCollectionHistory = invoiceData.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    const bolderTotals = (invoiceData.bolderData || []).reduce((acc, trip) => {
      const tons = Number(trip.netWeight || 0) / 1000;
      const amount = tons * 92;

      acc.count += 1;
      acc.totalTons += tons;
      acc.totalAmount += amount;
      return acc;
    }, { count: 0, totalTons: 0, totalAmount: 0 });

    const purchaseSummary = (invoiceData.purchases || []).reduce((acc, p) => {
      acc.total += Number(p.calculatedTotal || 0);
      acc.count += 1;
      return acc;
    }, { total: 0, count: 0 });


    // ✅ INCOME
    const totalCollection = invoiceData.payments.reduce(
      (sum, p) => sum + (Number(p.amount) || 0),
      0
    );

    const totalSaleAmount = invoiceData.trips.reduce(
      (sum, t) => sum + (Number(t.calculatedTotal) || 0),
      0
    );

    const totalIncome = totalSaleAmount + totalCollection;


    // ✅ EXPENSES
    const totalBolderExpense = bolderTotals.totalAmount || 0;

    const totalPurchaseExpense = purchaseSummary.total || 0;

    const totalOtherExpense = invoiceData.expenses?.grandTotal || 0;

    const totalLightBill = 56666; // static (you can make dynamic later)

    const totalStaffSalary = invoiceData.totalSalaryExpense || 0;
    const royaltyRate = 670;

    const totalRoyaltyExpense = totalQty * royaltyRate;

    const totalExpense =
      totalBolderExpense +
      totalPurchaseExpense +
      totalOtherExpense +
      totalLightBill +
      totalRoyaltyExpense +   // ✅ added
      totalStaffSalary;

    const totalFixedOtherExpense = totalLightBill + totalRoyaltyExpense;


    // ✅ PROFIT / LOSS
    const netProfit = totalIncome - totalExpense;



    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Grid className="p-2">
            <Box sx={{ border: '2px solid #000' }}>
              <View style={{ textAlign: 'center', marginBottom: 15, padding: 5 }}>
                {/* Company Name */}
                <Text style={{
                  fontSize: 18,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  textTransform: 'uppercase'
                }}>
                  TEJAS STONE CRUSHER
                </Text>


                {/* Address */}
                <Text style={{
                  fontSize: 10,
                  textAlign: 'center',
                  marginTop: 2
                }}>
                  Sarola, Dist. Dharashiv, Maharashtra
                </Text>

                {/* Report Title */}
                <Text style={{
                  fontSize: 11,
                  marginTop: 8,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  letterSpacing: 1,
                  textDecoration: 'underline'
                }}>
                  MASTER SALE & COLLECTION REPORT ({fromDate})
                </Text>

                {/* Custom Divider */}
                <View style={{
                  marginTop: 10,
                  marginBottom: 10,
                  borderBottomWidth: 2,
                  borderBottomColor: '#000',
                  width: '100%'
                }} />
              </View>

              {/* SECTION 1: TRIPS */}
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, marginTop: 10, color: "green", paddingLeft: 5 }}>
                I. TRIP SALES LOG
              </Text>

              <View style={{ display: 'table', width: '100%', borderStyle: 'solid', borderWidth: 1, borderColor: '#000', marginBottom: 10 }}>

                {/* TABLE HEADER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: '#000', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '8%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'center', borderRightWidth: 1 }}>Sr.</Text>
                  <Text style={{ width: '10%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'center', borderRightWidth: 1 }}>Tkt/Date</Text>
                  <Text style={{ width: '17%', fontSize: 8, fontWeight: 'bold', padding: 2, borderRightWidth: 1 }}>Customer</Text>
                  <Text style={{ width: '10%', fontSize: 8, fontWeight: 'bold', padding: 2, borderRightWidth: 1 }}>Vehicle</Text>
                  <Text style={{ width: '15%', fontSize: 8, fontWeight: 'bold', padding: 2, borderRightWidth: 1 }}>Product Item</Text>
                  <Text style={{ width: '5%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'center', borderRightWidth: 1 }}>Qty</Text>
                  <Text style={{ width: '10%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'right', borderRightWidth: 1 }}>Total</Text>

                  {/* Dynamic Payment Modes Header */}
                  {/* {paymentModeList.map((mode, i) => (
                    <Text key={i} style={{ width: '10%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'right', borderRightWidth: 1 }}>{mode}</Text>
                  ))} */}

                  <Text style={{ width: '8%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'right', borderRightWidth: 1, color: 'green' }}>Paid</Text>
                  <Text style={{ width: '8%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'right', color: 'red' }}>Pending</Text>
                </View>

                {/* TABLE BODY */}
                {invoiceData.trips.map((row, index) => (
                  <View key={row.id || index} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'stretch' }}>
                    <Text style={{ width: '8%', fontSize: 8, padding: 2, textAlign: 'center', borderRightWidth: 1 }}>{index + 1}</Text>
                    <View style={{ width: '10%', padding: 2, borderRightWidth: 1 }}>
                      <Text style={{ fontSize: 8, fontWeight: 'bold' }}>{row.ticketNo}</Text>
                      <Text style={{ fontSize: 6 }}>{row.grossDate ? row.grossDate.toDate().toLocaleDateString('en-GB') : "-"}</Text>
                    </View>
                    <Text style={{ width: '17%', fontSize: 8, padding: 2, borderRightWidth: 1 }}>{row.customerName}</Text>
                    <Text style={{ width: '10%', fontSize: 8, padding: 2, borderRightWidth: 1 }}>{row.vehicleNo}</Text>

                    {/* Product Items Sub-rows */}
                    <View style={{ width: '15%', borderRightWidth: 1 }}>
                      {(row.products || []).map((p, i) => (
                        <Text key={i} style={{ fontSize: 8, padding: 2, borderBottomWidth: i === row.products.length - 1 ? 0 : 0.5, borderBottomColor: '#eee' }}>
                          {p.productName}
                        </Text>
                      ))}
                    </View>

                    {/* Qty Sub-rows */}
                    <View style={{ width: '5%', borderRightWidth: 1 }}>
                      {(row.products || []).map((p, i) => (
                        <Text key={i} style={{ fontSize: 8, padding: 2, textAlign: 'center', borderBottomWidth: i === row.products.length - 1 ? 0 : 0.5, borderBottomColor: '#eee' }}>
                          {p.qty}
                        </Text>
                      ))}
                    </View>

                    <Text style={{ width: '10%', fontSize: 8, padding: 2, textAlign: 'right', borderRightWidth: 1 }}>
                      ₹{Number(row.totalAmount || 0).toLocaleString()}
                    </Text>

                    {/* Dynamic Payment Modes Values */}
                    {/* {paymentModeList.map((mode, i) => (
                      <Text key={i} style={{ width: '10%', fontSize: 8, padding: 2, textAlign: 'right', borderRightWidth: 1 }}>
                        ₹{getPaymentByMode(row, mode)}
                      </Text>
                    ))} */}

                    <Text style={{ width: '8%', fontSize: 8, padding: 2, textAlign: 'right', borderRightWidth: 1, color: 'green' }}>
                      ₹{Number(row.paidAmount || 0).toLocaleString()}
                    </Text>

                    <Text style={{ width: '8%', fontSize: 8, padding: 2, textAlign: 'right', color: 'red', fontWeight: 'bold' }}>
                      ₹{Number(row.remainingAmount || 0).toLocaleString()}
                    </Text>
                  </View>
                ))}

                {/* TABLE FOOTER SUMMARY */}
                <View style={{ flexDirection: 'row', backgroundColor: '#eeeeee', minHeight: 25, alignItems: 'center' }}>
                  <Text style={{ width: '60%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'right', borderRightWidth: 1 }}>
                    TOTAL SUMMARY
                  </Text>
                  <Text style={{ width: '5%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'center', borderRightWidth: 1 }}>
                    {totalQty.toFixed(2)}
                  </Text>
                  <Text style={{ width: '10%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'right', borderRightWidth: 1 }}>
                    ₹{invoiceData.trips.reduce((s, i) => s + (Number(i.totalAmount) || 0), 0).toLocaleString()}
                  </Text>

                  {/* {paymentModeList.map((mode, i) => (
                    <Text key={i} style={{ width: '8%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'right', borderRightWidth: 1 }}>
                      ₹{getTotalByMode(mode).toLocaleString()}
                    </Text>
                  ))} */}

                  <Text style={{ width: '8%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'right', borderRightWidth: 1, color: 'green' }}>
                    ₹{totalTripPaid.toLocaleString()}
                  </Text>
                  <Text style={{ width: '8%', fontSize: 8, fontWeight: 'bold', padding: 2, textAlign: 'right', color: 'red' }}>
                    ₹{(totalSale - totalTripPaid).toLocaleString()}
                  </Text>
                </View>
              </View>

              {/* SECTION 2: PAYMENTS */}
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, marginTop: 15, color: "green", paddingLeft: 5 }}>
                II. CUSTOMER COLLECTION HISTORY
              </Text>

              <View style={{ display: 'table', width: '100%', borderStyle: 'solid', borderWidth: 1, borderColor: '#000', marginBottom: 10 }}>

                {/* TABLE HEADER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: '#000', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '10%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>Sr.</Text>
                  <Text style={{ width: '25%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>Payment Date</Text>
                  <Text style={{ width: '30%', fontSize: 8, fontWeight: 'bold', padding: 4, borderRightWidth: 1 }}>Customer Name</Text>
                  <Text style={{ width: '15%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>Payment Mode</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'right' }}>Paid Amount</Text>
                </View>

                {/* TABLE BODY */}
                {invoiceData.payments.map((row, index) => (
                  <View key={row.id || index} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'center' }}>
                    <Text style={{ width: '10%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>{index + 1}</Text>

                    <Text style={{ width: '25%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>
                      {row.date ? (
                        (() => {
                          const d = row.date.toDate();
                          const day = String(d.getDate()).padStart(2, '0');
                          const month = String(d.getMonth() + 1).padStart(2, '0');
                          const year = d.getFullYear();
                          return `${day}-${month}-${year}`;
                        })()
                      ) : "-"}
                    </Text>

                    <Text style={{ width: '30%', fontSize: 8, padding: 4, borderRightWidth: 1 }}>
                      {row.displayCustomer}
                    </Text>

                    <Text style={{ width: '15%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>
                      {row.displayMode}
                    </Text>

                    <Text style={{ width: '20%', fontSize: 8, padding: 4, textAlign: 'right', fontWeight: 'bold' }}>
                      ₹{(Number(row.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Text>
                  </View>
                ))}

                {/* TABLE FOOTER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', minHeight: 25, alignItems: 'center' }}>
                  <Text style={{ width: '80%', fontSize: 9, fontWeight: 'bold', padding: 4, textAlign: 'right', borderRightWidth: 1 }}>
                    TOTAL COLLECTION
                  </Text>
                  <Text style={{ width: '20%', fontSize: 9, fontWeight: 'bold', padding: 4, textAlign: 'right' }}>
                    ₹{invoiceData.payments.reduce((s, i) => s + (Number(i.amount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>
              </View>

              {/* SECTION 3: PRODUCT SUMMARY */}
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, marginTop: 15, color: "green", paddingLeft: 5 }}>
                III. PRODUCT WISE SUMMARY
              </Text>

              <View style={{ display: 'table', width: '100%', borderStyle: 'solid', borderWidth: 1, borderColor: '#000' }}>

                {/* TABLE HEADER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: '#000', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '10%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>Sr.</Text>
                  <Text style={{ width: '35%', fontSize: 8, fontWeight: 'bold', padding: 4, borderRightWidth: 1 }}>Product Name</Text>
                  <Text style={{ width: '15%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>Trip Count</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'right', borderRightWidth: 1 }}>Total Quantity</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'right' }}>Total Sales Value</Text>
                </View>

                {/* TABLE BODY */}
                {invoiceData.products.map((row, index) => (
                  <View key={index} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'center' }}>
                    <Text style={{ width: '10%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>{index + 1}</Text>
                    <Text style={{ width: '35%', fontSize: 8, padding: 4, borderRightWidth: 1 }}>{row.name}</Text>
                    <Text style={{ width: '15%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>{row.count}</Text>
                    <Text style={{ width: '20%', fontSize: 8, padding: 4, textAlign: 'right', borderRightWidth: 1 }}>{row.qty.toFixed(2)}</Text>
                    <Text style={{ width: '20%', fontSize: 8, padding: 4, textAlign: 'right', fontWeight: 'bold' }}>
                      ₹{row.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Text>
                  </View>
                ))}

                {/* TABLE FOOTER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', minHeight: 25, alignItems: 'center' }}>
                  <View style={{ width: '10%', borderRightWidth: 1, minHeight: 25 }} />
                  <Text style={{ width: '35%', fontSize: 8, fontWeight: 'bold', padding: 4, borderRightWidth: 1 }}>TOTAL</Text>
                  <Text style={{ width: '15%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>{productTotals.count}</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'right', borderRightWidth: 1 }}>{productTotals.qty.toFixed(2)}</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'right' }}>
                    ₹{productTotals.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>
              </View>

              {/* SECTION V: PURCHASE EXPENSE */}
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, marginTop: 15, color: "red", paddingLeft: 5 }}>
                IV. PURCHASE EXPENSE
              </Text>

              <View style={{ display: 'table', width: '100%', borderStyle: 'solid', borderWidth: 1, borderColor: '#000' }}>

                {/* TABLE HEADER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: '#000', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '8%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>Sr.</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>Date</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, borderRightWidth: 1 }}>Purchase No</Text>
                  <Text style={{ width: '32%', fontSize: 8, fontWeight: 'bold', padding: 4, borderRightWidth: 1 }}>Dealer</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'right' }}>Purchase Total</Text>
                </View>

                {/* TABLE BODY */}
                {(invoiceData.purchases || []).map((row, index) => (
                  <View key={index} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'center' }}>
                    <Text style={{ width: '8%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>{index + 1}</Text>

                    <Text style={{ width: '20%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>
                      {row.purchasedate}
                    </Text>

                    <Text style={{ width: '20%', fontSize: 8, padding: 4, fontWeight: 'bold', borderRightWidth: 1 }}>
                      {row.purchaseno}
                    </Text>

                    <Text style={{ width: '32%', fontSize: 8, padding: 4, fontWeight: 'bold', borderRightWidth: 1 }}>
                      {row.displayDealer}
                    </Text>

                    <Text style={{ width: '20%', fontSize: 8, padding: 4, textAlign: 'right', fontWeight: 'bold' }}>
                      ₹{(row.calculatedTotal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Text>
                  </View>
                ))}

                {/* PURCHASE TOTAL FOOTER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', minHeight: 25, alignItems: 'center' }}>
                  <Text style={{ width: '48%', fontSize: 9, fontWeight: 'bold', padding: 4, textAlign: 'right', borderRightWidth: 1 }}>
                    TOTAL PURCHASES ({purchaseSummary.count}):
                  </Text>
                  <View style={{ width: '32%', borderRightWidth: 1, minHeight: 25 }} />
                  <Text style={{ width: '20%', fontSize: 9, fontWeight: 'bold', padding: 4, textAlign: 'right', color: 'red' }}>
                    ₹{purchaseSummary.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>
              </View>
              {/* SECTION VI: OTHER EXPENSES */}
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, marginTop: 15, color: "red", paddingLeft: 5 }}>
                V. OTHER EXPENSES
              </Text>

              <View style={{ display: 'table', width: '100%', borderStyle: 'solid', borderWidth: 1, borderColor: '#000' }}>

                {/* TABLE HEADER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: '#000', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '8%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>SR</Text>
                  <Text style={{ width: '25%', fontSize: 8, fontWeight: 'bold', padding: 4, borderRightWidth: 1 }}>Expense Head</Text>
                  <Text style={{ width: '15%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>Date</Text>
                  <Text style={{ width: '32%', fontSize: 8, fontWeight: 'bold', padding: 4, borderRightWidth: 1 }}>Description</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'right' }}>Amount</Text>
                </View>

                {/* TABLE BODY */}
                {(invoiceData.expenses?.list || []).map((row, index) => {
                  const headName = row.headName ||
                    expenseHeads.find(h => h.id === row.expenseHeadId)?.name ||
                    row.expenseHeadId;

                  return (
                    <View key={row.id || index} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'center' }}>
                      <Text style={{ width: '8%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>{index + 1}</Text>

                      <Text style={{ width: '25%', fontSize: 8, padding: 4, borderRightWidth: 1 }}>
                        {headName}
                      </Text>

                      <Text style={{ width: '15%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>
                        {row.displayDate || (row.date?.toDate ? row.date.toDate().toLocaleDateString('en-GB') : "-")}
                      </Text>

                      <Text style={{ width: '32%', fontSize: 8, padding: 4, borderRightWidth: 1 }}>
                        {row.description || "-"}
                      </Text>

                      <Text style={{ width: '20%', fontSize: 8, padding: 4, textAlign: 'right' }}>
                        ₹{Number(row.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Text>
                    </View>
                  );
                })}

                {/* TOTAL FOOTER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', minHeight: 25, alignItems: 'center' }}>
                  <View style={{ width: '8%', borderRightWidth: 1, minHeight: 25 }} />
                  <Text style={{ width: '72%', fontSize: 9, fontWeight: 'bold', padding: 4, textAlign: 'right', borderRightWidth: 1 }}>
                    TOTAL OTHER EXPENSES:
                  </Text>
                  <Text style={{ width: '20%', fontSize: 9, fontWeight: 'bold', padding: 4, textAlign: 'right', color: 'red' }}>
                    ₹{(invoiceData.expenses?.grandTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>
              </View>
              {/* SECTION V: LIGHT BILL EXPENSES */}
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, marginTop: 15, color: "red", paddingLeft: 5 }}>
                VI. OTHER EXPENSES
              </Text>

              <View style={{ display: 'table', width: '100%', borderStyle: 'solid', borderWidth: 1, borderColor: '#000' }}>

                {/* TABLE HEADER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: '#000', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '8%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>SR</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, borderRightWidth: 1 }}>Type</Text>
                  <Text style={{ width: '15%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'center', borderRightWidth: 1 }}>Date</Text>
                  <Text style={{ width: '37%', fontSize: 8, fontWeight: 'bold', padding: 4, borderRightWidth: 1 }}>Description</Text>
                  <Text style={{ width: '20%', fontSize: 8, fontWeight: 'bold', padding: 4, textAlign: 'right' }}>Amount</Text>
                </View>

                {/* TABLE BODY */}
                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '8%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>1</Text>
                  <Text style={{ width: '20%', fontSize: 8, padding: 4, borderRightWidth: 1 }}>Light Bill</Text>
                  <Text style={{ width: '15%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>
                    {new Date().toLocaleDateString('en-GB')}
                  </Text>
                  <Text style={{ width: '37%', fontSize: 8, padding: 4, borderRightWidth: 1 }}>Daily Electricity Charges</Text>
                  <Text style={{ width: '20%', fontSize: 8, padding: 4, textAlign: 'right' }}>
                    ₹{Number(56666).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '8%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>1</Text>
                  <Text style={{ width: '20%', fontSize: 8, padding: 4, borderRightWidth: 1 }}>Royalty Expense</Text>
                  <Text style={{ width: '15%', fontSize: 8, padding: 4, textAlign: 'center', borderRightWidth: 1 }}>
                    {new Date().toLocaleDateString('en-GB')}
                  </Text>
                  <Text style={{ width: '37%', fontSize: 8, padding: 4, borderRightWidth: 1 }}>Royalty Expense ({totalQty.toFixed(2)} X 670)</Text>
                  <Text style={{ width: '20%', fontSize: 8, padding: 4, textAlign: 'right' }}>
                    ₹{totalRoyaltyExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>

                {/* TOTAL FOOTER */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', minHeight: 25, alignItems: 'center' }}>
                  <View style={{ width: '8%', borderRightWidth: 1, minHeight: 25 }} />
                  <Text style={{ width: '72%', fontSize: 9, fontWeight: 'bold', padding: 4, textAlign: 'right', borderRightWidth: 1 }}>
                    TOTAL OTHER EXPENSES:
                  </Text>
                  <Text style={{ width: '20%', fontSize: 9, fontWeight: 'bold', padding: 4, textAlign: 'right', color: 'red' }}>
                    ₹{totalFixedOtherExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>
              </View>


              {/* SECTION VII: STAFF SALARY (DAILY) */}
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, marginTop: 15, color: "red", paddingLeft: 5 }}>
                VII. STAFF SALARY (DAILY)
              </Text>

              <View style={{ display: 'table', width: '100%', borderStyle: 'solid', borderWidth: 1, borderColor: '#000' }}>

                {/* TOTAL FOOTER ROW */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', minHeight: 25, alignItems: 'center' }}>
                  <Text style={{
                    width: '80%',
                    fontSize: 9,
                    fontWeight: 'bold',
                    padding: 4,
                    textAlign: 'right',
                    borderRightWidth: 1
                  }}>
                    TOTAL SALARY EXPENSE:
                  </Text>

                  <Text style={{
                    width: '20%',
                    fontSize: 9,
                    fontWeight: 'bold',
                    padding: 4,
                    textAlign: 'right',
                    color: 'red'
                  }}>
                    ₹{(invoiceData.totalSalaryExpense || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>
              </View>
              {/* SECTION VIII: FINAL SUMMARY */}
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, marginTop: 15, color: "blue", paddingLeft: 5 }}>
                VIII. FINAL SUMMARY
              </Text>

              <View style={{ display: 'table', width: '100%', borderStyle: 'solid', borderWidth: 1, borderColor: '#000' }}>

                {/* INCOME SECTION */}
                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '70%', fontSize: 9, padding: 4, fontWeight: 'bold', borderRightWidth: 1 }}>Total Sale</Text>
                  <Text style={{ width: '30%', fontSize: 9, padding: 4, textAlign: 'right' }}>
                    ₹{totalSaleAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'center' }}>
                  <Text style={{ width: '70%', fontSize: 9, padding: 4, fontWeight: 'bold', borderRightWidth: 1 }}>Total Collection</Text>
                  <Text style={{ width: '30%', fontSize: 9, padding: 4, textAlign: 'right' }}>
                    ₹{totalCollection.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', backgroundColor: '#e3f2fd', borderBottomWidth: 1, borderBottomColor: '#000', minHeight: 22, alignItems: 'center' }}>
                  <Text style={{ width: '70%', fontSize: 9, padding: 4, fontWeight: 'bold', borderRightWidth: 1 }}>TOTAL INCOME</Text>
                  <Text style={{ width: '30%', fontSize: 9, padding: 4, textAlign: 'right', fontWeight: 'bold', color: 'green' }}>
                    ₹{totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>

                {/* EXPENSE SECTION */}
                {[
                  { label: 'Bolder Expense', value: totalBolderExpense },
                  { label: 'Purchase Expense', value: totalPurchaseExpense },
                  { label: 'Other Expense', value: totalOtherExpense },
                  { label: 'Light Bill', value: totalLightBill },
                  { label: 'Staff Salary', value: totalStaffSalary },
                ].map((item, idx) => (
                  <View key={idx} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd', minHeight: 20, alignItems: 'center' }}>
                    <Text style={{ width: '70%', fontSize: 9, padding: 4, borderRightWidth: 1 }}>{item.label}</Text>
                    <Text style={{ width: '30%', fontSize: 9, padding: 4, textAlign: 'right' }}>
                      ₹{item.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Text>
                  </View>
                ))}

                <View style={{ flexDirection: 'row', backgroundColor: '#ffebee', borderBottomWidth: 1, borderBottomColor: '#000', minHeight: 22, alignItems: 'center' }}>
                  <Text style={{ width: '70%', fontSize: 9, padding: 4, fontWeight: 'bold', borderRightWidth: 1 }}>TOTAL EXPENSE</Text>
                  <Text style={{ width: '30%', fontSize: 9, padding: 4, textAlign: 'right', fontWeight: 'bold', color: 'red' }}>
                    ₹{totalExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', backgroundColor: 'rgb(182, 255, 196)', borderBottomWidth: 1, borderBottomColor: '#000', minHeight: 22, alignItems: 'center' }}>
                  <Text style={{ width: '70%', fontSize: 9, padding: 4, fontWeight: 'bold', borderRightWidth: 1 }}>NET PROFIT</Text>
                  <Text style={{ width: '30%', fontSize: 9, padding: 4, textAlign: 'right', fontWeight: 'bold', color: 'black' }}>
                    ₹{netProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </View>

              </View>

            </Box>
          </Grid>


        </Page>
      </Document>
    );
  };

  const isMobile = window.innerWidth <= 768;


  return (
    <div>
      <nav
        className="main-header navbar navbar-expand navbar-orange navbar-light"
        style={{
          background: "linear-gradient(90deg, #060cbd, #054d90)",
          padding: "0.5rem 1rem",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          marginLeft: isMobile
            ? "0"
            : collapsed
              ? "80px"
              : "260px",

          width: isMobile
            ? "100%"
            : collapsed
              ? "calc(100% - 80px)"
              : "calc(100% - 260px)",
          transition: "0.3s ease",
          position: "fixed",
          top: 0,
          right: 0,
          zIndex: 999
        }}
      >
        {/* Left navbar links */}
        <ul className="navbar-nav">
          <li className="nav-item">
            <button
              type="button"
              onClick={() => {
                if (window.innerWidth <= 768) {
                  setMobileMenuOpen(prev => !prev);
                } else {
                  setCollapsed(prev => !prev);
                }
              }}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: "18px"
              }}
            >
              <i className="fas fa-bars"></i>
            </button>
          </li>
          <li className="nav-item">
            <a className="nav-link" href="#0">
              <b style={{ color: "#fff", textTransform: "uppercase", fontSize: 16 }}>
                Tejas Stone Crusher
              </b>
            </a>
          </li>
        </ul>

        {/* Right navbar links */}
        <ul className="navbar-nav ml-auto" style={{ marginLeft: "auto" }}>
          {/* Sync Button */}
          <li className="nav-item">
            <a
              className="nav-link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.location.reload();
              }}
              title="Refresh Data"
            >
              <i className="fas fa-sync" style={{ color: "#fff", fontSize: 16 }}></i>
            </a>
          </li>

          {/* HIDE ON MOBILE: Navigation Buttons */}
          <li className="nav-item d-none d-md-block">
            <a className="nav-link" href="javascript:history.go(-1)" title="Go Back">
              <i className="fas fa-backward" style={{ color: "#fff", fontSize: 16 }} />
            </a>
          </li>

          <li className="nav-item d-none d-md-block">
            <a className="nav-link" href="javascript:history.go(+1)" title="Go Forward">
              <i className="fas fa-forward" style={{ color: "#fff", fontSize: 16 }} />
            </a>
          </li>


          {/* Sign Out */}
          <li className="nav-item">
            <a className="nav-link" onClick={signOut} href="#0" title="Sign Out">
              <i className="fa fa-sign-out" style={{ color: "#fff", fontSize: 16 }} />
            </a>
          </li>
        </ul>
      </nav>

      {/* Internal CSS */}
      <style>{`
        .navbar-nav .nav-link:hover {
          color: #ffd700 !important;
          transition: all 0.3s ease;
        }
        .navbar-nav .nav-item {
          margin-left: 0.8rem;
          display: flex;
          align-items: center;
        }
        @media (max-width: 768px) {
          .main-header .navbar-nav {
            flex-direction: row !important;
          }
          b { font-size: 13px !important; }
          .nav-item { margin-left: 0.4rem; }
        }
      `}</style>
    </div >
  );
}

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 10
  },
});


export default Header;